import {
  TellescopeModelFromName,
  useAuth,
  useCachedTags,
  usePermissionQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  cn,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  DatePicker,
  DialogFooter,
  Input,
  Label,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Textarea,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { StateSelectField, useForm } from "@tellescope/forms";
import { createKeyMap, formatDateTime } from "@tellescope/utils";
import { toast } from "sonner";
import { useCallback, useMemo, useState } from "react";
import { CustomFields } from "@tellescope/types-models";
import { CustomFieldInput, CustomFieldValue } from "./CustomFieldInput";
import { ReadOnlyField } from "./ReadOnlyField";
import { Plus, X } from "lucide-react";

const CREATE_TAG_OPTION_VALUE = "__create-tag__";

export interface InformationSectionProps {
  contact: TellescopeModelFromName<"endusers">;
  onNoteEditingChange?: (isEditing: boolean) => void;
}

export const InformationSection = ({ contact, onNoteEditingChange }: InformationSectionProps) => {
  const { t } = useTranslation("contacts");
  const { format } = useDateFnsFormattersWithLocale();
  const { selectedOrganization, currentUser } = useAuth();

  const [customFieldChanges, setCustomFieldChanges] = useState<CustomFields>({});
  const [tagValue, setTagValue] = useState("");
  const [noteEditValue, setNoteEditValue] = useState<string>();

  const { data: enduserTags } = useCachedTags({
    model: "endusers",
  });

  const {
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "endusers",
    record: contact,
    actions: ["update"],
  });
  const readOnly = !canUpdate;

  const updateContactMutation = useTellescopeMutation({
    model: "endusers",
    method: "updateOne",
    onSuccess: () => {
      setCustomFieldChanges({});
    },
    onError: () => {
      toast.error(t("Failed to update contact. Please try again."));
    },
    optimisticUpdates: true,
  });

  const fieldConfigs = useMemo(
    () => createKeyMap(selectedOrganization?.settings?.endusers?.builtinFields ?? [], "field"),
    [selectedOrganization?.settings?.endusers?.builtinFields]
  );

  const visibleCustomFields = useMemo(
    () => (selectedOrganization?.settings?.endusers?.customFields ?? []).filter((f) => !f.hiddenFromProfile),
    [selectedOrganization?.settings?.endusers?.customFields]
  );

  const getCustomFieldValue = useCallback(
    (fieldKey: string): CustomFieldValue | undefined => {
      if (fieldKey in customFieldChanges) return customFieldChanges[fieldKey];
      return contact.fields?.[fieldKey];
    },
    [customFieldChanges, contact.fields]
  );

  const handleCustomFieldChange = useCallback((fieldKey: string, next: CustomFieldValue) => {
    setCustomFieldChanges((prev) => ({ ...prev, [fieldKey]: next }));
  }, []);

  const handleAddTag = useCallback(
    (rawTag: string) => {
      const tag = rawTag.trim();
      if (!tag) return;
      const current = contact.tags ?? [];
      if (current.some((existing) => existing.toLowerCase() === tag.toLowerCase())) {
        setTagValue("");
        return;
      }
      updateContactMutation.mutate({
        id: contact.id,
        tags: [tag],
      });
      setTagValue("");
    },
    [contact.id, contact.tags, updateContactMutation]
  );

  const handleRemoveTag = useCallback(
    (tag: string) => {
      updateContactMutation.mutate({
        id: contact.id,
        tags: (contact.tags ?? []).filter((existing) => existing !== tag),
        _options: { replaceObjectFields: true },
      });
    },
    [contact.id, contact.tags, updateContactMutation]
  );

  const startEditingNote = useCallback(() => {
    setNoteEditValue(contact.note ?? "");
    onNoteEditingChange?.(true);
  }, [contact.note, onNoteEditingChange]);

  const stopEditingNote = useCallback(() => {
    setNoteEditValue(undefined);
    onNoteEditingChange?.(false);
  }, [onNoteEditingChange]);

  const trimmedTagValue = tagValue.trim();

  const tagSuggestions = useMemo(
    () => enduserTags.filter((t) => t.toLowerCase().includes(tagValue.toLowerCase()) && !contact.tags?.includes(t)),
    [contact.tags, enduserTags, tagValue]
  );

  const canCreateTag =
    trimmedTagValue.length > 0 &&
    !(contact.tags ?? []).some((tag) => tag.toLowerCase() === trimmedTagValue.toLowerCase()) &&
    !tagSuggestions.some((tag) => tag.toLowerCase() === trimmedTagValue.toLowerCase());

  const isTagPopoverOpen = trimmedTagValue.length > 0 && (tagSuggestions.length > 0 || canCreateTag);

  const isCustomFieldsDirty = useMemo(
    () =>
      Object.entries(customFieldChanges).some(
        ([key, value]) => JSON.stringify(value) !== JSON.stringify(contact.fields?.[key])
      ),
    [customFieldChanges, contact.fields]
  );

  const form = useForm({
    fields: {
      fname: {
        type: "text",
        required: fieldConfigs["fname"]?.required,
        initialValue: contact.fname || "",
      },
      lname: {
        type: "text",
        required: fieldConfigs["lname"]?.required,
        initialValue: contact.lname || "",
      },
      email: {
        type: "email",
        required: fieldConfigs["email"]?.required,
        initialValue: contact.email || "",
      },
      // TODO: come up with better phone input
      phone: {
        type: "text",
        required: fieldConfigs["phone"]?.required,
        initialValue: contact.phone || "",
      },
      dateOfBirth: {
        type: "date",
        required: fieldConfigs["dateOfBirth"]?.required,
        initialValue: contact.dateOfBirth ? new Date(contact.dateOfBirth) : undefined,
      },
      // TODO: come up with better insurance input
      primaryInsurance: {
        type: "text",
        required: fieldConfigs["primaryInsurance"]?.required,
        initialValue: contact.insurance?.planName || "",
      },
      // TODO: come up with better insurance input
      secondaryInsurance: {
        type: "text",
        required: fieldConfigs["secondaryInsurance"]?.required,
        initialValue: contact.insuranceSecondary?.planName || "",
      },
      addressLineOne: {
        type: "text",
        initialValue: contact.addressLineOne,
        required: fieldConfigs["addressLineOne"]?.required,
      },
      addressLineTwo: {
        type: "text",
        initialValue: contact.addressLineTwo,
        required: fieldConfigs["addressLineTwo"]?.required,
      },
      city: {
        type: "text",
        initialValue: contact.city,
        required: fieldConfigs["city"]?.required,
      },
      state: {
        type: "select",
        initialValue: contact.state,
        required: fieldConfigs["state"]?.required,
      },
      zipCode: {
        type: "text",
        initialValue: contact.zipPlusFour ? `${contact.zipCode}-${contact.zipPlusFour}` : contact.zipCode,
        required: fieldConfigs["zipCode"]?.required,
      },
    },
    validateOn: "change",
    validators: {
      email: (value) => {
        if (value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
          return t("Invalid email address");
        }
        return undefined;
      },
      phone: (value) => {
        if (value && !/^\+?[1-9]\d{1,14}$/.test(value)) {
          return t("Invalid phone number");
        }
        return undefined;
      },
      zipCode: (val) => {
        if (!val) return undefined;
        return /^\d{5}(-\d{4})?$/.test(val) ? undefined : t("Please enter a valid zip code");
      },
    },
    onSubmit: (values) => {
      const [zipCode, zipCodePlusFour] = values.zipCode.split("-");
      const mergedFields: CustomFields | undefined = isCustomFieldsDirty
        ? { ...(contact.fields ?? {}), ...customFieldChanges }
        : undefined;
      updateContactMutation.mutate({
        id: contact.id,
        fname: values.fname,
        lname: values.lname,
        email: values.email,
        phone: values.phone,
        dateOfBirth: values.dateOfBirth ? format(values.dateOfBirth, "MM-dd-yyyy") : "",
        insurance: {
          ...contact.insurance,
          planName: values.primaryInsurance,
        },
        insuranceSecondary: {
          ...contact.insuranceSecondary,
          planName: values.secondaryInsurance,
        },
        addressLineOne: values.addressLineOne,
        addressLineTwo: values.addressLineTwo,
        city: values.city,
        state: values.state,
        zipCode: zipCode,
        zipPlusFour: zipCodePlusFour || "",
        ...(mergedFields ? { fields: mergedFields } : {}),
      });
    },
  });

  return (
    <form
      className={cn("grid grid-cols-1 md:grid-cols-2 gap-4", (form.isDirty || isCustomFieldsDirty) && "pb-12")}
      {...form.getFormProps()}
    >
      {!(fieldConfigs["tags"]?.hidden ?? false) && (
        <div className="space-y-2 col-span-2">
          <span className="flex gap-2 items-center flex-wrap">
            {contact.tags?.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                onClick={() => {
                  if (canUpdate) {
                    handleRemoveTag(tag);
                  }
                }}
                className={cn(
                  "font-normal text-xs text-primary",
                  canUpdate && "hover:bg-gray-200 transition-all cursor-pointer "
                )}
              >
                {tag}
                {canUpdate && <X size={6} />}
              </Badge>
            ))}
          </span>
          {canUpdate && (
            <Command shouldFilter={false} className="overflow-visible bg-transparent">
              <Popover open={isTagPopoverOpen}>
                <PopoverAnchor asChild>
                  <Input
                    value={tagValue}
                    onChange={(e) => setTagValue(e.target.value)}
                    className="border-0 hover:bg-gray-100"
                    placeholder={t("Add tags...")}
                  />
                </PopoverAnchor>
                <PopoverContent
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                >
                  <CommandList>
                    <CommandGroup>
                      {canCreateTag && (
                        <CommandItem value={CREATE_TAG_OPTION_VALUE} onSelect={() => handleAddTag(trimmedTagValue)}>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                          {t('Add "{{tagName}}"', { tagName: trimmedTagValue })}
                        </CommandItem>
                      )}
                      {tagSuggestions.map((tag) => (
                        <CommandItem key={tag} value={tag} onSelect={() => handleAddTag(tag)}>
                          {tag}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </PopoverContent>
              </Popover>
            </Command>
          )}
        </div>
      )}
      {!(fieldConfigs["note"]?.hidden ?? false) ? (
        !canUpdate && !contact.note ? null : (
          <div className="col-span-2 space-y-1">
            {noteEditValue === undefined ? (
              <span className="flex gap-0 items-stretch group">
                <div
                  className={cn(
                    "w-[2px] self-stretch bg-gray-300 group-focus-within:bg-transparent transition-all duration-300 shrink-0 my-2",
                    !contact.note && "bg-transparent",
                    canUpdate && "group-hover:bg-transparent"
                  )}
                />
                <Button
                  variant="ghost"
                  className={cn(
                    "justify-start w-full h-auto py-2 px-3 font-light focus:bg-gray-100 whitespace-pre-wrap text-left disabled:cursor-text disabled:opacity-100 disabled:pointer-events-auto disabled:bg-transparent disabled:select-text",
                    !contact.note && "text-muted-foreground hover:text-muted-foreground",
                    !canUpdate && "text-muted-foreground/60 hover:text-muted-foreground/60 italic"
                  )}
                  onClick={() => {
                    if (canUpdate) {
                      startEditingNote();
                    }
                  }}
                  disabled={!canUpdate}
                >
                  {contact.note || t("Notes")}
                </Button>
              </span>
            ) : (
              <Textarea
                autoFocus
                className="min-h-0 border-0 bg-gray-100 ml-[2px] resize-none"
                placeholder={t("Notes")}
                style={{
                  fieldSizing: "content",
                }}
                value={noteEditValue}
                onChange={(e) => setNoteEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    updateContactMutation.mutate(
                      {
                        id: contact.id,
                        note: noteEditValue,
                      },
                      { onSuccess: stopEditingNote }
                    );
                  }
                  if (e.key === "Escape") {
                    stopEditingNote();
                  }
                }}
                onBlur={stopEditingNote}
              />
            )}

            <p
              className={cn(
                "transition-all text-xs text-muted-foreground font-light duration-300",
                noteEditValue === undefined ? "opacity-0" : "opacity-100"
              )}
            >
              {t("Enter to save, Esc to cancel")}
            </p>
          </div>
        )
      ) : null}
      {!(fieldConfigs["fname"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="fname" required={fieldConfigs["fname"]?.required}>
            {t("First Name")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="fname" value={contact.fname} placeholder={t("Not provided")} />
          ) : (
            <Input id="fname" {...form.getFieldProps("fname")} />
          )}
        </div>
      )}
      {!(fieldConfigs["lname"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="lname" required={fieldConfigs["lname"]?.required}>
            {t("Last Name")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="lname" value={contact.lname} placeholder={t("Not provided")} />
          ) : (
            <Input id="lname" {...form.getFieldProps("lname")} />
          )}
        </div>
      )}
      {!(fieldConfigs["email"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="email" required={fieldConfigs["email"]?.required}>
            {t("Email")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="email" value={contact.email} placeholder={t("Not provided")} />
          ) : (
            <Input id="email" {...form.getFieldProps("email")} />
          )}
        </div>
      )}
      {!(fieldConfigs["phone"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="phone" required={fieldConfigs["phone"]?.required}>
            {t("Phone")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="phone" value={contact.phone} placeholder={t("Not provided")} />
          ) : (
            <Input id="phone" {...form.getFieldProps("phone")} />
          )}
        </div>
      )}
      {!(fieldConfigs["dateOfBirth"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="dob" required={fieldConfigs["dateOfBirth"]?.required}>
            {t("Date of Birth")}
          </Label>
          {readOnly ? (
            <ReadOnlyField
              id="dob"
              value={contact.dateOfBirth ? format(new Date(contact.dateOfBirth), "MM/dd/yyyy") : undefined}
              placeholder={t("Not provided")}
            />
          ) : (
            <DatePicker
              id="dob"
              {...form.getCustomFieldProps("dateOfBirth")}
              placeholder={contact?.dateOfBirth ? format(new Date(contact.dateOfBirth), "MM/dd/yyyy") : "Not provided"}
              className="space-y-0"
              label=""
            />
          )}
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="dateAdded">{t("Date Added")}</Label>
        <ReadOnlyField id="dateAdded" value={formatDateTime(contact.createdAt, "MM/DD/YY at HH:mm:ss")} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dateUpdated">{t("Date Updated")}</Label>
        <ReadOnlyField id="dateUpdated" value={formatDateTime(contact.createdAt, "MM/DD/YY at HH:mm:ss")} />
      </div>
      {!(fieldConfigs["primaryInsurance"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="primaryInsurance" required={fieldConfigs["primaryInsurance"]?.required}>
            {t("Primary Insurance")}
          </Label>
          {/* TODO: swap back to an editable input once we have better input/validation */}
          <ReadOnlyField id="primaryInsurance" value={contact.insurance?.planName} placeholder={t("Not provided")} />
        </div>
      )}
      {!(fieldConfigs["secondaryInsurance"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="secondaryInsurance" required={fieldConfigs["secondaryInsurance"]?.required}>
            {t("Secondary Insurance")}
          </Label>
          {/* TODO: swap back to an editable input once we have better input/validation */}
          <ReadOnlyField
            id="secondaryInsurance"
            value={contact.insuranceSecondary?.planName}
            placeholder={t("Not provided")}
          />
        </div>
      )}
      {!(fieldConfigs["addressLineOne"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="address" required={fieldConfigs["addressLineOne"]?.required}>
            {t("Address")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="address" value={contact.addressLineOne} placeholder={t("Not provided")} />
          ) : (
            <Input id="address" {...form.getFieldProps("addressLineOne")} />
          )}
        </div>
      )}
      {!(fieldConfigs["addressLineTwo"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="addressLineTwo" required={fieldConfigs["addressLineTwo"]?.required}>
            {t("Address Line 2")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="addressLineTwo" value={contact.addressLineTwo} placeholder={t("Not provided")} />
          ) : (
            <Input id="addressLineTwo" {...form.getFieldProps("addressLineTwo")} />
          )}
        </div>
      )}
      {!(fieldConfigs["city"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="city" required={fieldConfigs["city"]?.required}>
            {t("City")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="city" value={contact.city} placeholder={t("Not provided")} />
          ) : (
            <Input id="city" {...form.getFieldProps("city")} />
          )}
        </div>
      )}
      {!(fieldConfigs["state"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="state" required={fieldConfigs["state"]?.required}>
            {t("State")}
          </Label>
          {readOnly ? (
            <ReadOnlyField id="state" value={contact.state} placeholder={t("Not provided")} />
          ) : (
            <StateSelectField {...form.getCustomFieldProps("state")} className="flex-1" />
          )}
        </div>
      )}
      {!(fieldConfigs["zipCode"]?.hidden ?? false) && (
        <div className="space-y-1">
          <Label htmlFor="zipCode" required={fieldConfigs["zipCode"]?.required}>
            {t("ZIP Code")}
          </Label>
          {readOnly ? (
            <ReadOnlyField
              id="zipCode"
              value={contact.zipPlusFour ? `${contact.zipCode}-${contact.zipPlusFour}` : contact.zipCode}
              placeholder={t("Not provided")}
            />
          ) : (
            <Input id="zipCode" {...form.getFieldProps("zipCode")} />
          )}
        </div>
      )}
      {visibleCustomFields.length > 0 && (
        <Accordion type="single" collapsible className="col-span-2">
          <AccordionItem value="custom-fields" className="border-b-0">
            <AccordionTrigger>{t("Custom Fields")}</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                {visibleCustomFields.map((customField) => {
                  const inputId = `custom-${customField.field}`;
                  return (
                    <div key={customField.field} className="space-y-1">
                      <Label htmlFor={inputId} required={customField.required}>
                        {customField.field}
                      </Label>
                      <CustomFieldInput
                        id={inputId}
                        customField={customField}
                        value={getCustomFieldValue(customField.field)}
                        onChange={(next) => handleCustomFieldChange(customField.field, next)}
                        userId={currentUser?.id}
                        readOnly={readOnly}
                      />
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      {(form.isDirty || isCustomFieldsDirty) && (
        <DialogFooter variant="floating">
          <div className="flex justify-between items-center w-full">
            <p className="font-semibold text-md">{t("Update Contact Information")}</p>
            <div className="flex gap-2 justify-end items-center">
              <Button
                onClick={() => {
                  form.resetForm();
                  setCustomFieldChanges({});
                }}
                variant="outline"
              >
                {t("Cancel")}
              </Button>
              <Button type="submit" disabled={!form.isValid}>
                {t("Save")}
              </Button>
            </div>
          </div>
        </DialogFooter>
      )}
    </form>
  );
};
